import Action from '@/common/action'
import { Interface } from '@/shared/di/interface'
import { ISwipeArea, ITouch } from '@/shared/di/interface/touch'
import { WalletContact } from '@/state/vault/domain'
import gsap, { Back, Sine } from 'gsap'
import { Group, Vector2, Vector3 } from 'three'
import { container } from 'tsyringe'
import { SpawnParticleOptions } from '../particle-system/global-particles-system'
import RoomPhysicsBase from './room-physics-base'
import Wallet from './wallet'
import { IAssets } from '@/shared/di/interface/assets'
import Tube from './tube'
import { IWalletFace, IWalletFaces } from '@/shared/di/interface/walletfaces'
import { ISound } from '@/shared/di/interface/sound'

const sound = container.resolve<ISound>(Interface.ISound)

const tempVector3_1 = new Vector3()
const tempVector3_2 = new Vector3()

export enum WalletType {
  None = 'none',
  Contact = 'contact',
  User = 'user',
  Tjing = 'tjing',
  Remove = 'remove'
}
export default class WalletManager {
  tubeContainer = new Group()
  #physics: RoomPhysicsBase
  #walletOpen = false
  #contactTube: Wallet | undefined
  #userTube: Wallet | undefined
  #tjingTube: Tube | undefined
  #removeTube: Tube | undefined
  #currentWalletType = WalletType.None
  #hasLoadedTjingWallet = false
  activeContactWalletId = ''
  #lastEmitTime = 0
  #tubeToTypeMap = new Map<WalletType, Wallet | Tube>()
  #tubePosition = new Vector2()
  #activeTween: gsap.core.Tween | undefined
  lastContactWalletFaceId = ''
  lastUserWalletFaceId: string | undefined

  onSpawnParticle: Action<SpawnParticleOptions> = new Action()
  onCloseTube: Action<void> = new Action()

  constructor(physics: RoomPhysicsBase, tubePosition: Vector2) {
    this.#tubePosition.copy(tubePosition)
    this.#physics = physics
    this.tubeContainer.position.y = 1
  }

  init() {
    this.createContactTube()
    this.createUserTube()
    this.createTjingTube()
    this.createRemoveTube()
  }

  setUserName(userName: string) {
    this.#userTube!.setName(userName)
  }

  createContactTube() {
    this.#contactTube = new Wallet(WalletType.Contact, this.#tubePosition)
    this.#tubeToTypeMap.set(WalletType.Contact, this.#contactTube)

    if (this.#contactTube.model) {
      this.#contactTube.model.visible = false
      this.tubeContainer.add(this.#contactTube.model)
    }
  }

  createUserTube() {
    this.#userTube = new Wallet(WalletType.User, this.#tubePosition)
    this.#tubeToTypeMap.set(WalletType.User, this.#userTube)

    if (this.#userTube.model) {
      this.#userTube.model.visible = false
      this.tubeContainer.add(this.#userTube.model)
    }
  }

  createTjingTube() {
    this.#tjingTube = new Tube(WalletType.Tjing, this.#tubePosition.clone().add(new Vector2(0, -0.4)))
    this.#tubeToTypeMap.set(WalletType.Tjing, this.#tjingTube)

    window.setTimeout(() => {
      this.#tjingTube!.setAvailable(true)
    }, 100)

    this.#tjingTube.setName('Tjing')
    if (this.#tjingTube.model) {
      this.#tjingTube.model.visible = false
      this.tubeContainer.add(this.#tjingTube.model)
    }
  }

  createRemoveTube() {
    this.#removeTube = new Tube(WalletType.Remove, this.#tubePosition.clone().add(new Vector2(0, -0.4)))
    this.#tubeToTypeMap.set(WalletType.Remove, this.#removeTube)
    if (this.#removeTube.model) {
      this.#removeTube.model.visible = false
      this.tubeContainer.add(this.#removeTube.model)
    }
  }

  swapWalletTypeModel(type: WalletType) {
    if (this.#currentWalletType === type) {
      return
    }

    this.#tubeToTypeMap.forEach(wallet => {
      if (wallet.model) {
        wallet.model.visible = false
      }
    })

    this.#currentWalletType = type

    if (this.#currentWalletType !== WalletType.None) {
      const model = this.#tubeToTypeMap.get(this.#currentWalletType)!.model
      if (model) {
        model.visible = true
      }
    }
  }

  changeUser = (contact: WalletContact): Promise<void> => {
    if (!this.#contactTube) {
      return Promise.resolve()
    }

    this.#contactTube.setAvailable(contact.availableForTransfer)
    this.#contactTube.setOnline(contact.online)
    this.#contactTube.emitParticles = contact.availableForTransfer
    if (contact.availableForTransfer) {
      this.#physics.interactions.setActiveUserAvailable(true)
    }

    this.activeContactWalletId = contact.walletId

    if (contact.uxPub.walletFaceId && this.lastContactWalletFaceId !== contact.uxPub.walletFaceId) {
      const assets = container.resolve<IAssets>(Interface.IAssets)
      assets.unloadNamespace('wallet-contact-' + this.lastContactWalletFaceId)
      this.lastContactWalletFaceId = contact.uxPub.walletFaceId

      const walletfaces = container.resolve<IWalletFaces>(Interface.IWalletFaces)
      return walletfaces.loadWalletFace(contact.uxPub.walletFaceId).then(() => {
        const face: IWalletFace = walletfaces.getWalletFace(contact.uxPub.walletFaceId)
        if (face.outside.assets && face.outside.assets.length > 0) {
          assets.loadManifest(face.outside.assets, 'wallet-contact-' + contact.uxPub.walletFaceId).then(() => {
            this.#contactTube!.setConfig(face.outside)
            this.#contactTube!.setName(contact.name)
            return this.doOpen(WalletType.Contact)
          })
        }
      })
    } else {
      this.#contactTube!.setName(contact.name)
      return this.doOpen(WalletType.Contact)
    }
  }

  showContactWallet(contact: WalletContact | undefined): Promise<void> {
    this.#physics.interactions.setActiveUserAvailable(false)
    if (!this.#contactTube || !contact) {
      return Promise.resolve()
    }

    if (
      this.currentWalletType === WalletType.Tjing ||
      this.activeContactWalletId !== contact.walletId ||
      this.lastContactWalletFaceId !== contact.uxPub.walletFaceId
    ) {
      return this.hideActiveWallet().then(() => {
        return this.changeUser(contact)
      })
    } else {
      this.#contactTube.setAvailable(contact.availableForTransfer)
      this.#contactTube.emitParticles = contact.availableForTransfer

      return this.doOpen(WalletType.Contact)
    }
  }

  /*showUserWallet(walletFaceId: string) {
    if (this.lastUserWalletFaceId !== walletFaceId) {
      const assets = container.resolve<IAssets>(Interface.IAssets)
      assets.unloadNamespace('wallet-user-' + this.lastUserWalletFaceId)
      this.lastUserWalletFaceId = walletFaceId
      const walletOutsideConfig = walletFaces[walletFaceId!].walletOutsideConfig as IWalletOutsideConfig
      if (walletOutsideConfig.manifest && walletOutsideConfig.manifest.length > 0) {
        assets.loadManifest(walletOutsideConfig.manifest, 'wallet-user-' + walletFaceId!).then(() => {
          this.#userTube!.setConfig(walletOutsideConfig)
          return this.hideActiveWallet().then(() => {
            this.doOpen(WalletType.User)
          })
        })
      }
    } else {
      this.lastUserWalletFaceId = walletFaceId
      this.hideActiveWallet().then(() => {
        this.doOpen(WalletType.User)
      })
    }
  }*/

  showTjingWallet() {
    if (this.currentWalletType === WalletType.Tjing) {
      this.doOpen(WalletType.Tjing)
      return
    }

    /*if (!this.#hasLoadedTjingWallet) {
      const assets = container.resolve<IAssets>(Interface.IAssets)
      assets.unloadNamespace('wallet-user-' + this.lastUserWalletFaceId)

      const walletOutsideConfig = walletFaces['gold'].walletOutsideConfig as IWalletOutsideConfig
      if (walletOutsideConfig.manifest && walletOutsideConfig.manifest.length > 0) {
        assets.loadManifest(walletOutsideConfig.manifest, 'tjing-wallet').then(() => {
          this.#tjingTube!.setConfig(walletOutsideConfig)
          this.#hasLoadedTjingWallet = true
          return this.hideActiveWallet().then(() => {
            this.doOpen(WalletType.Tjing)
          })
        })
      }
    } else {
      this.hideActiveWallet().then(() => {
        this.doOpen(WalletType.Tjing)
      })
    }*/
    this.hideActiveWallet().then(() => {
      this.doOpen(WalletType.Tjing)
    })
  }

  showRemoveWallet() {
    this.hideActiveWallet().then(() => {
      this.doOpen(WalletType.Remove)
    })
  }

  updateTubeCoins(coinsInTubes: Map<string, number>) {
    if (this.#contactTube && coinsInTubes.has(this.activeContactWalletId)) {
      const coinsInThisTube = coinsInTubes.get(this.activeContactWalletId) || 0
      if (coinsInThisTube > 0) {
        this.#contactTube.setIncomingAssets(true)
      } else {
        this.#contactTube.setIncomingAssets(false)
      }
    }
  }

  addCloseContactSwipe() {
    const touch = container.resolve<ITouch>(Interface.ITouch)
    const area: ISwipeArea = {
      id: 'close-contact',
      originPercentageRect: { x: 0, y: 0, width: 1, height: 0.15 },
      angle: Math.PI * -0.5,
      angleMargin: Math.PI * 0.5,
      distance: 0.05,
      priority: 2,
      callback: () => {
        if (this.#physics.interactions.isDragging) {
          return
        }

        this.hideActiveWallet()
        this.#currentWalletType = WalletType.None
        this.onCloseTube.invoke()
      }
    }

    touch.registerSwipeArea(area)
  }

  doOpen(toType: WalletType): Promise<void> {
    return new Promise(resolve => {
      this.swapWalletTypeModel(toType)

      if (toType === WalletType.None) {
        this.activeContactWalletId = ''
      }

      if (this.#currentWalletType === WalletType.None) {
        this.#walletOpen = false
        resolve()
      } else {
        if (this.#walletOpen) {
          return Promise.resolve()
        }
        this.#walletOpen = true

        if (this.#activeTween) {
          this.#activeTween.kill()
        }

        gsap.delayedCall(0.1, () => {
          this.#tubeToTypeMap.get(toType)!.open()
        })

        sound.play('wallet-drop')?.rate(0.8 + Math.random() * 0.4)

        this.#activeTween = gsap.to(this.tubeContainer.position, {
          duration: 0.3,
          y: -1,
          ease: Back.easeOut.config(1),
          onStart: () => {
            this.#physics.interactions.enableTube()

            if (this.#currentWalletType === WalletType.Contact || this.#currentWalletType === WalletType.Remove) {
              this.addCloseContactSwipe()
            }
          },
          onComplete: () => {
            this.#physics.enableTopSensor()
            resolve()
          },
          onUpdate: () => {
            this.#physics.updateTopGroupPosition(
              this.tubeContainer.position.y - (this.#currentWalletType === WalletType.Remove ? 1.4 : 0)
            )
          }
        })
      }
    })
  }

  hideActiveWallet = (): Promise<void> => {
    if (!this.#walletOpen) {
      return Promise.resolve()
    }

    this.#walletOpen = false

    return new Promise(resolve => {
      if (this.#currentWalletType !== WalletType.None) {
        this.#tubeToTypeMap.get(this.#currentWalletType)!.close()
        this.#currentWalletType = WalletType.None
      }

      container.resolve<ITouch>(Interface.ITouch).unregisterSwipeArea('close-contact')
      this.#physics.interactions.disableTube()
      this.#physics.disableTopSensor()

      if (this.#activeTween) {
        this.#activeTween.kill()
      }

      sound.play('wallet-drop')?.rate(0.8 + Math.random() * 0.4)

      this.#activeTween = gsap.to(this.tubeContainer.position, {
        duration: 0.3,
        y: 10,
        delay: 0.25,
        ease: Sine.easeIn,
        onUpdate: () => {
          this.#physics.updateTopGroupPosition(this.tubeContainer.position.y)
        },
        onComplete: () => {
          //this.#currentWalletType = WalletType.None
          resolve()
        }
      })
    })
  }

  removeActiveWallet = () => {
    if (this.#currentWalletType !== WalletType.None) {
      this.#tubeToTypeMap.get(this.#currentWalletType)!.reset()
    }

    container.resolve<ITouch>(Interface.ITouch).unregisterSwipeArea('close-contact')
    this.#physics.interactions.disableTube()
    this.#physics.disableTopSensor()

    if (this.#activeTween) {
      this.#activeTween.kill()
    }

    this.tubeContainer.position.y = 10
    this.#physics.updateTopGroupPosition(this.tubeContainer.position.y)
  }

  animateCoinIn() {
    this.#tubeToTypeMap.get(this.#currentWalletType)?.animateCoinIn()
  }

  animateCoinOut() {
    this.#tubeToTypeMap.get(this.#currentWalletType)?.animateCoinOut()
  }

  update(time: number, delta: number) {
    if (this.#currentWalletType !== WalletType.None) {
      this.#tubeToTypeMap.get(this.#currentWalletType)?.update(time, delta)
      this.updateTubeParticles()
    }
  }

  setCoinNearTube(distance: number) {
    if (this.#currentWalletType !== WalletType.None) {
      this.#tubeToTypeMap.get(this.#currentWalletType)!.setCoinNearTube(distance)
    }
  }

  updateTubeParticles() {
    if (this.#contactTube && this.#contactTube.emitParticles) {
      const model = this.#contactTube.model
      if (model) {
        tempVector3_1.copy(model.position)
        tempVector3_1.y -= 7
        tempVector3_1.x += Math.random() * 6 - 3
        tempVector3_1.z = 4

        const now = Date.now()
        if (this.#lastEmitTime + 50 > now) {
          return
        }

        this.#lastEmitTime = now
        this.onSpawnParticle.invoke({
          position: tempVector3_1,
          velocity: tempVector3_2.set(-tempVector3_1.x * 0.7, 4.2, 0),
          size: 1.5,
          lifetime: 1.4,
          velocityRandomness: 0,
          positionRandomness: 0.5,
          smoothPosition: true,
          turbulence: 0.1
        })
      }
    }
  }

  get currentWalletType() {
    return this.#currentWalletType
  }

  destroy() {
    this.onSpawnParticle.removeAll()
    this.onCloseTube.removeAll()
  }
}
